import {
  DynamicPageVariant,
  MarketingApproach,
  marketingApproaches,
} from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Modal, { ModalActionData } from '../components/Common/Modal';
import Select from '../components/Common/Select';
import TextArea from '../components/Common/TextArea';
import TextField from '../components/Common/TextField';
import ConstraintsForm from '../components/Constraints/ConstraintsForm';
import { DashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import { createNewDynamicPageVariant } from '../services/DynamicPagesService';
import { camelCaseToTitle } from '../utils';

export type CreateVariantData = Partial<
  Pick<DynamicPageVariant, 'constraints' | 'description' | 'name'> & {
    approach: MarketingApproach;
    prompt: string;
  }
>;

interface Props {
  defaultData?: CreateVariantData;
  open: boolean;
  onClose: () => void;
}

export default function CreateVariantModal({
  defaultData,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [data, setData] = useState<CreateVariantData>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (open) return;

    setData({});
    setCreating(false);
    setError(undefined);
  }, [open]);

  const createVariant = useCallback(async () => {
    if (!selectedAccount || !selectedDynamicPage) return;
    if (creating) return;

    const name = data.name;

    if (!name) {
      setError(new Error('Please provide a name for the variant'));
      return;
    }

    setCreating(true);

    try {
      await createNewDynamicPageVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        name,
        data.description,
        data.approach || 'emotional',
        data.constraints || {},
        data.prompt,
      );

      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setCreating(false);
  }, [
    creating,
    data.approach,
    data.constraints,
    data.description,
    data.name,
    data.prompt,
    onClose,
    selectedAccount,
    selectedDynamicPage,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createVariant(),
      },
    ],
    [creating, onClose, createVariant],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="xl">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Create a new variant
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name*"
          readOnly={creating}
          placeholder="A name for the variant"
          value={data.name ?? defaultData?.name ?? ''}
          onChange={(e) => {
            setData((d) => {
              d.name = e.target.value;
              return { ...d };
            });
          }}
        />
        <TextArea
          id="description"
          name="description"
          title="Description (optional)"
          placeholder="A brief description of the variant"
          rows={4}
          value={data.description ?? defaultData?.description ?? ''}
          onChange={(e) => {
            setData((d) => {
              d.description = e.target.value;
              return { ...d };
            });
          }}
        />
        <Select
          title="Marketing approach"
          placeholder="select your approach..."
          selected={data.approach ?? defaultData?.approach}
          disabled={creating}
          onChange={(e) => {
            setData((d) => {
              d.approach = e;
              return { ...d };
            });
          }}
          options={marketingApproaches.map((approach) => ({
            value: approach,
            child: camelCaseToTitle(approach),
          }))}
        />
        <TextArea
          id="prompt"
          name="prompt"
          title="Prompt (optional)"
          placeholder="Optional constraints for the variant creation"
          rows={4}
          value={data.prompt ?? defaultData?.prompt ?? ''}
          onChange={(e) => {
            setData((d) => {
              d.prompt = e.target.value;
              return { ...d };
            });
          }}
        />
        <hr className="border-gray-200" />
        <label className="text-sm font-semibold text-gray-900">
          Constraints
        </label>
        <ConstraintsForm
          constraints={data.constraints ?? defaultData?.constraints ?? {}}
          onChange={(constraints) => {
            setData((d) => {
              d.constraints = constraints;
              return { ...d };
            });
          }}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
